import { useState } from 'react';
import { SlidersHorizontal } from 'lucide-react';

import { Button } from '@/components/ui/Button';
import { Meter } from '@/components/ui/Meter';
import { Switch } from '@/components/ui/Switch';
import { CardLimitsForm } from '@/components/cards/CardLimitsForm';
import { useSetCardEnabled } from '@/lib/mutations';
import { toMessage } from '@/lib/api';
import { blockReasonFor, resolveCard, usageRatio } from '@/lib/cards';
import { rub } from '@/lib/format';
import { CARD_BLOCK_HINT, CARD_BLOCK_LABEL } from '@/lib/moneyLabels';
import { cn } from '@/lib/cn';
import type { TraderCard } from '@/types/api';

/**
 * Whether this card is taking payments, how close it is to its ceilings, and
 * the controls for both.
 *
 * The availability line comes from `cardBlockReason` in the shared package —
 * the same function the router calls. That is the whole point: a card must
 * never sit here saying it is available while routing quietly skips it, and the
 * only way to guarantee that is to ask the same question of the same code.
 */
export function CardControls({ card }: { card: TraderCard }) {
  const { isEnabled, limits, usage } = resolveCard(card);
  const blockReason = blockReasonFor(card);
  const setEnabled = useSetCardEnabled();
  const [isEditing, setIsEditing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const stateId = `card-state-${card.id}`;
  const isAvailable = blockReason === null;

  const toggle = (next: boolean): void => {
    setFailure(null);
    setEnabled.mutate(
      { cardId: card.id, isEnabled: next },
      {
        onError: (error) =>
          setFailure(
            toMessage(error, 'Не удалось изменить карту. Состояние осталось прежним.'),
          ),
      },
    );
  };

  return (
    <div className="mt-3 rounded-md border border-line bg-surface px-4 py-3.5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-base font-medium text-fg">
            <span
              aria-hidden="true"
              className={cn(
                'h-2 w-2 shrink-0 rounded-full',
                isAvailable ? 'bg-ok-500' : 'bg-warn-500',
              )}
            />
            <span className="min-w-0">
              {isAvailable ? 'Принимает платежи' : CARD_BLOCK_LABEL[blockReason]}
            </span>
          </p>
          <p id={stateId} className="mt-1 max-w-prose text-sm leading-normal text-fg-secondary">
            {isAvailable
              ? 'Карта готова принять следующий платёж.'
              : CARD_BLOCK_HINT[blockReason]}
          </p>
        </div>

        <Switch
          checked={isEnabled}
          onChange={toggle}
          disabled={setEnabled.isPending}
          label={`Приём на карту ${card.bank}, оканчивающуюся на ${card.last4}`}
          describedBy={stateId}
        />
      </div>

      {failure ? (
        <p role="alert" className="mt-2 text-sm text-danger-500">
          {failure}
        </p>
      ) : null}

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <Meter
          label="За сутки"
          ratio={usageRatio(usage.usedRub24h, limits.dailyLimitRub)}
          value={
            limits.dailyLimitRub > 0
              ? `${rub(usage.usedRub24h)} из ${rub(limits.dailyLimitRub)}`
              : `${rub(usage.usedRub24h)} · без ограничения`
          }
        />
        <Meter
          label="Одновременных платежей"
          ratio={usageRatio(usage.activeCount, limits.concurrentLimit)}
          value={limits.concurrentLimit === 0 ? `${usage.activeCount} / без лимита` : `${usage.activeCount} из ${limits.concurrentLimit}`}
        />
      </div>

      <p className="mt-3 text-xs text-fg-muted">
        На один платёж:{' '}
        <span className="num">
          {limits.perPaymentLimitRub > 0 ? rub(limits.perPaymentLimitRub) : 'без ограничения'}
        </span>
      </p>

      {isEditing ? (
        <CardLimitsForm cardId={card.id} limits={limits} onDone={() => setIsEditing(false)} />
      ) : (
        <div className="mt-4">
          <Button
            size="sm"
            icon={<SlidersHorizontal size={13} strokeWidth={1.5} aria-hidden="true" />}
            onClick={() => setIsEditing(true)}
          >
            Изменить лимиты
          </Button>
        </div>
      )}
    </div>
  );
}
